import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  isValidSalaryEstimate,
  type JobDetails,
} from '@xing-com/crate-jobs-helpers';
import { useBreakpoints } from '@xing-com/crate-jobs-hooks';
import { useExperiment } from '@xing-com/hub';

import { JDPModule, ModuleVisibility } from '../types';

type ModulesVisibilityReturn = {
  visibleModules: ModuleVisibility;
  modulesList: string;
};

const jobHasHeaderImage = (jobDetails: JobDetails): boolean => {
  // Check if the job description is of "TemplateData" type and contains a
  // header image URL
  if (
    jobDetails.description.__typename === 'TemplateData' &&
    !!jobDetails.description.headerImage
  )
    return true;

  // Check if the job description was compiled by XJM and contains a section
  // with the "header-image" class. False otherwise (which covers all the other
  // cases)
  return !!(
    jobDetails.description.__typename === 'HtmlDescription' &&
    jobDetails.description.content?.includes('<section class="header-image">')
  );
};

export const useModulesVisibility = (
  jobDetails: JobDetails,
  hasMatchingData: boolean
): ModulesVisibilityReturn => {
  const { isLoggedIn } = useLoginState();
  const { isMediaSmallOrTiny } = useBreakpoints();
  const shouldHideKununuData = useExperiment('ABACUS-518') === 'B';
  const {
    company,
    employerData,
    paid,
    redirectsToThirdPartyUrl,
    salary,
    summaryData,
  } = jobDetails;

  const visibleModules: ModuleVisibility = {
    Header: true,
    AiSummary: !!summaryData && !redirectsToThirdPartyUrl, // NW1 already checks isLoggedIn and paid job to return summaryData
    MatchingSkills: hasMatchingData && !redirectsToThirdPartyUrl,
    MatchingBanner:
      paid &&
      !isLoggedIn &&
      !jobHasHeaderImage(jobDetails) &&
      !isMediaSmallOrTiny &&
      !redirectsToThirdPartyUrl,
    Description: true,
    Salary:
      !!salary && !redirectsToThirdPartyUrl && isValidSalaryEstimate(salary),
    Company: !!company?.id && !redirectsToThirdPartyUrl,
    Culture:
      !shouldHideKununuData && !!employerData && !redirectsToThirdPartyUrl,
    Contacts: !!company?.id && isLoggedIn && !redirectsToThirdPartyUrl,
    SearchAlerts: !redirectsToThirdPartyUrl,
    SimilarJobs: !redirectsToThirdPartyUrl,
  };

  const modulesList = Object.keys(visibleModules)
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    .filter((key) => visibleModules[key as JDPModule])
    .join(',');

  return {
    visibleModules,
    modulesList,
  };
};
